import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import '../styles/DrumApp.scss';
import { MidiContext } from '../store/MidiContext';
import { useMidiDrums } from '../hooks/useMidiDrums';
import { useKoreanDom } from '../hooks/useKoreanDom';
import PurchaseGate from './PurchaseGate';
import {
  drumLoggedInAtom,
  drumLanguageAtom,
  drumPurchaseTargetAtom,
  drumPurchaseStateAtom,
  drumActivePlanAtom,
} from '../store/drumAppAtoms';
import { useMemberSession } from '../hooks/useMemberSession';
import { getDifficultyData } from '../utils/drumHelpers';

function DrumAppRoot() {
  const loggedIn = useAtomValue(drumLoggedInAtom);
  const language = useAtomValue(drumLanguageAtom);
  const [purchaseTarget, setPurchaseTarget] = useAtom(drumPurchaseTargetAtom);
  const [purchaseState, setPurchaseState] = useAtom(drumPurchaseStateAtom);
  const [, setActivePlan] = useAtom(drumActivePlanAtom);
  const [showMidiNotice, setShowMidiNotice] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const midi = useMidiDrums(loggedIn);
  useMemberSession();

  useKoreanDom(language, `${loggedIn}|${location.pathname}`);

  useEffect(() => {
    if (!loggedIn && !location.pathname.endsWith('/login')) {
      navigate('/drum/login', { replace: true });
    }
  }, [loggedIn, location.pathname, navigate]);

  useEffect(() => {
    if (!loggedIn || !['connected', 'disconnected', 'unsupported', 'denied'].includes(midi.status)) return undefined;
    setShowMidiNotice(true);
    const timer = window.setTimeout(() => setShowMidiNotice(false), 4200);
    return () => window.clearTimeout(timer);
  }, [loggedIn, midi.status]);

  const runPurchase = () => {
    setPurchaseState('checking');
    window.setTimeout(() => setPurchaseState('processing'), 650);
    window.setTimeout(() => {
      setActivePlan('drummer');
      setPurchaseState('success');
    }, 1550);
  };

  const restorePurchase = () => {
    setPurchaseState('checking');
    window.setTimeout(() => {
      setActivePlan('drummer');
      setPurchaseState('restored');
    }, 1100);
  };

  return (
    <div className="drum-app" data-localized-root key={language}>
      <MidiContext.Provider value={midi}>
        <Outlet />
      </MidiContext.Provider>
      {showMidiNotice && (
        <div className={`midi-entry-notice ${midi.status === 'connected' ? 'connected' : 'disconnected'}`} role="status">
          <i />
          <span>
            <strong>{language === 'ko' ? (midi.status === 'connected' ? 'MIDI 드럼이 연결되었습니다' : 'MIDI 드럼이 연결되지 않았습니다') : (midi.status === 'connected' ? 'MIDI drum connected' : 'MIDI drum not connected')}</strong>
            <small>{midi.status === 'connected' ? (midi.deviceName || (language === 'ko' ? '드럼 입력을 사용할 수 있습니다' : 'Drum input is ready')) : (language === 'ko' ? '화면 터치는 계속 사용할 수 있습니다' : 'Touch controls are still available')}</small>
          </span>
        </div>
      )}
      {purchaseTarget && (
        <PurchaseGate
          song={purchaseTarget.song}
          difficulty={(language === 'ko' ? getDifficultyData(purchaseTarget.song, purchaseTarget.difficulty)?.labelKo : getDifficultyData(purchaseTarget.song, purchaseTarget.difficulty)?.labelEn) || ''}
          language={language}
          state={purchaseState}
          onPurchase={runPurchase}
          onRestore={restorePurchase}
          onRetry={runPurchase}
          onCancel={() => setPurchaseTarget(null)}
        />
      )}
    </div>
  );
}

export default DrumAppRoot;
